import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ActionReducerMap, StoreModule } from '@ngrx/store';
import { localStorageSync } from 'ngrx-store-localstorage';
import { HotToastModule } from '@ngneat/hot-toast';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from 'src/environments/environment';

import { SharedModule } from './shared/shared.module';
import { RegistrationModule } from './registration/registration.module';
import { AccountModule } from './account/account.module';
import { AppRoutingModule } from './app-routing.module';
import { ShopModule } from './shop/shop.module';
import { CheckoutModule } from './checkout/checkout.module';

import { AppComponent } from './app.component';
import { shoppingCartReducer } from './state/shopping-cart.reducer';
import { regDataReducer } from './state/reg-data.reducer';
import { AppState } from './state/app.state';
import { authReducer } from './state/auth.reducer';
import { ConfigHelper } from './core/services/helpers/config.helper';
import { DateTimeHelper } from './core/services/helpers/date-time.helper';
import { LoadingService } from './core/services/loading.service';
import { StepProgressService } from './core/services/step-progress.service';
import { StorageHelper } from './core/services/helpers/storage.helper';
import { SmsHelper } from './core/services/helpers/smsHelper';
import { LoadingInterceptor } from './core/services/interceptors/loading.interceptor';
import { AuthInterceptor } from './core/services/interceptors/auth.interceptor';
import { myAccountReducer } from './state/my-account.reducer';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { businessAccountParentKeyReducer } from './state/business-account-parent-key.reducer';
import { resetStoreReducer } from './state/reset-store.reducer';
import { BrandEnum } from './core/dtos/enum';
import { redirectUrlReducer } from './state/redirect-url.reducer';
import { Shared404RoutingModule } from './shared/shared-404.module';
import { GoogleAnalyticsService } from './core/services/google-analytics.service';


const reducers: ActionReducerMap<AppState> = {
  auth: authReducer,
  shoppingCart: shoppingCartReducer,
  regData: regDataReducer,
  myAccount: myAccountReducer,
  businessAccountParentKey: businessAccountParentKeyReducer,
  redirectUrl: redirectUrlReducer
};

const localStorageSyncReducer = reducer =>
  localStorageSync({ keys: ["auth", "regData", "shoppingCart", "myAccount", "businessAccountParentKey", "redirectUrl"], rehydrate: true, storage: sessionStorage })(reducer);

const metaReducers = [localStorageSyncReducer, resetStoreReducer];

const configHelper = new ConfigHelper();
const brandId = configHelper.getBrandId();
const colour = brandId === BrandEnum.CAEL ? '#a6192e' : '#baa86b';

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    StoreModule.forRoot(reducers, { metaReducers }),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode
    }),
    HotToastModule.forRoot({
      dismissible: true,
      duration: 5000,
      style: {
        backgroundColor: colour,
        maxWidth: '100%',
        width: '100%',
        color: 'white',
      },
      position: 'top-center',
      iconTheme: {
        primary: '#FFFAEE',
        secondary: '#4BB543',
      },
      error: {
        style: {
          backgroundColor: '#f8d7da',
          color: 'red',
          maxWidth: '100%',
          width: '100%',
        },
        icon: '',
        iconTheme: {
          primary: '#FFFAEE',
          secondary: '#FF0000',
        },
      },
      warning: {
        style: {
          color: 'red',
          backgroundColor: '#f8d7da',
          maxWidth: '100%',
          width: '100%',
        },
        iconTheme: {
          primary: '#FFFAEE',
          secondary: '#FF0000',
        },
      }
    }),
    ShopModule,
    CheckoutModule,
    AccountModule,
    RegistrationModule,
    SharedModule,
    AppRoutingModule,
    Shared404RoutingModule,
  ],
  providers: [
    ConfigHelper,
    DateTimeHelper,
    LoadingService,
    StepProgressService,
    StorageHelper,
    SmsHelper,
    GoogleAnalyticsService,
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
